// Spin the Bottle / Truth or Dare — a party game for 3–12 around a rug.
//
// Pure rules (imports only ./types.ts and the card deck), so the same file
// runs in the `games` edge function, in the browser and under node tests.
//
// A turn:
//   spin      the current spinner spins (button, or flick the bottle in VR).
//             The SERVER picks who it lands on: uniformly among everyone
//             seated except the spinner. Idle 30 s → it spins by itself.
//   spinning  every client animates the bottle to the server's result
//             (g.spin); the game clock moves on when the animation ends.
//   choose    Truth or Dare? Truth-or-Dare mode: the target picks. Spin the
//             Bottle mode: the spinner picks for the target. Idle → random.
//   card      a card from the deck (never repeated until the pool is used
//             up) is shown to everyone. The target taps Done or Pass (a few
//             passes per game); the others may clap (+1 each, once per turn)
//             or vote to skip the card (majority → a fresh card).
//   cheer     a few seconds after Done for the claps to land, then the
//             target becomes the next spinner.
// Points: Dare done +2, Truth done +1, each clap +1, pass 0.
// Rounds: a round is as many turns as there are players; "endless" plays on
// until a majority votes to end (or fewer than 2 players remain). Every way
// the game ends goes through phase 'over', so the leaderboard records it.

import { RuleError, type Ctx, type GameDef, type GameStep, type Privs, type SeatResult } from './types.ts';
import { TRUTHS, DARES, type CardSet } from './bottle-cards.ts';

export const MAX_PLAYERS = 12;
export const MIN_PLAYERS = 3;
export const SPIN_IDLE_MS = 30_000;
export const CHOOSE_MS = 25_000;
export const CARD_MS = 90_000;
export const CHEER_MS = 4_000;
export const PASS_LIMIT = 2;
export const ROUND_CHOICES = [0, 1, 2, 3, 5] as const; // 0 = endless

export type BottleMode = 'tod' | 'spin';
export type Spice = 'mild' | 'silly';
export type CardType = 'truth' | 'dare';

export interface BottleOptions { mode: BottleMode; spice: Spice; rounds: number }
export const DEFAULT_OPTIONS: BottleOptions = { mode: 'tod', spice: 'mild', rounds: 0 };

export interface BottlePlayer {
  uid: string | null;
  name: string;
  score: number;
  passes: number; // passes used
  truths: number;
  dares: number;
  claps: number;  // claps received
}

export interface Spin {
  /** Spin number (clients start a new animation when it changes). */
  n: number;
  by: number;
  /** Seat (= cushion) the bottle ends pointing at. */
  to: number;
  /** Whole extra turns before it settles (cosmetic). */
  turns: number;
  /** Animation length. */
  ms: number;
  /** Server time it started. */
  at: number;
}

export interface Card { id: string; type: CardType; text: string; n: number }

export interface BottleState {
  phase: 'spin' | 'spinning' | 'choose' | 'card' | 'cheer' | 'over';
  mode: BottleMode;
  spice: Spice;
  rounds: number;
  passLimit: number;
  round: number;
  turnInRound: number;
  turnNo: number;
  spinner: number;
  target: number | null;
  chooser: number | null;
  spin: Spin | null;
  card: Card | null;
  /** Seats that clapped this turn. */
  claps: number[];
  /** Seats voting to skip the current card. */
  skipVotes: number[];
  /** Seats voting to end the game. */
  endVotes: number[];
  /** Game clock deadline for the current phase. */
  endsAt: number | null;
  players: Record<number, BottlePlayer>;
  /** Stats of players who left, by user id — restored if they sit back down. */
  away: Record<string, Omit<BottlePlayer, 'uid' | 'name'>>;
  /** Card ids already drawn, per type (cleared when a pool is exhausted). */
  used: { truth: string[]; dare: string[] };
  /** How the previous turn ended (for the UI). */
  last: { seat: number; name: string; result: 'done' | 'pass' | 'timeout' | 'left'; type: CardType | null; points: number } | null;
  /** Why the game ended. */
  ended: 'rounds' | 'vote' | 'players' | null;
}

export type BottleMove =
  | { type: 'spin'; power?: number }
  | { type: 'choose'; pick: CardType }
  | { type: 'done' }
  | { type: 'pass' }
  | { type: 'clap' }
  | { type: 'skip' }
  | { type: 'end'; on?: boolean };

// ---- the deck ----------------------------------------------------------------

/** Card ids → text for a type at a spice level ('silly' adds the silly cards). */
export function poolOf(type: CardType, spice: Spice): { id: string; text: string }[] {
  const set: CardSet = type === 'truth' ? TRUTHS : DARES;
  const t = type === 'truth' ? 't' : 'd';
  const out = set.mild.map((text, i) => ({ id: `${t}m${i}`, text }));
  if (spice === 'silly') set.silly.forEach((text, i) => out.push({ id: `${t}s${i}`, text }));
  return out;
}

/** Draw a card nobody has seen yet this game (the pool reshuffles when exhausted). */
export function drawCard(g: BottleState, type: CardType, rng: () => number): { card: Card; used: BottleState['used'] } {
  const pool = poolOf(type, g.spice);
  let seen = new Set(g.used[type]);
  let left = pool.filter((c) => !seen.has(c.id));
  if (!left.length) { seen = new Set(); left = pool; }
  const pick = left[Math.min(left.length - 1, Math.floor(rng() * left.length))];
  const usedList = [...seen, pick.id];
  return {
    card: { id: pick.id, type, text: pick.text, n: (g.card?.n ?? 0) + 1 },
    used: { ...g.used, [type]: usedList },
  };
}

// ---- helpers -------------------------------------------------------------------

export const seatedList = (seats: Ctx['seats']): number[] =>
  seats.map((s, i) => (s ? i : -1)).filter((i) => i >= 0);

const majority = (votes: number, players: number) => players > 0 && votes * 2 > players;

/** The next seated player clockwise after `from` (not `from` itself if others exist). */
export function nextSeated(seats: Ctx['seats'], from: number): number {
  const n = seats.length;
  for (let k = 1; k <= n; k++) {
    const i = (((from + k) % n) + n) % n;
    if (seats[i]) return i;
  }
  return from;
}

/** Fair target: uniform over every seated player except the spinner. */
export function pickTarget(seats: Ctx['seats'], spinner: number, rng: () => number): number {
  const others = seatedList(seats).filter((i) => i !== spinner);
  if (!others.length) throw new RuleError('Nobody else to spin to');
  return others[Math.min(others.length - 1, Math.floor(rng() * others.length))];
}

function newPlayer(ctx: Ctx, seat: number, away?: BottleState['away']): BottlePlayer {
  const s = ctx.seats[seat];
  const uid = s?.kind === 'human' ? s.userId ?? null : null;
  const back = uid && away ? away[uid] : undefined;
  return { uid, name: s?.name ?? `Seat ${seat + 1}`, score: 0, passes: 0, truths: 0, dares: 0, claps: 0, ...(back ?? {}) };
}

function readOptions(o: any): BottleOptions {
  const mode: BottleMode = o?.mode === 'spin' ? 'spin' : 'tod';
  const spice: Spice = o?.spice === 'silly' ? 'silly' : 'mild';
  const rounds = (ROUND_CHOICES as readonly number[]).includes(o?.rounds) ? Number(o.rounds) : 0;
  return { mode, spice, rounds };
}

const without = (list: number[], seat: number) => list.filter((s) => s !== seat);

/** Start the next turn with `spinner` spinning. */
function toSpin(g: BottleState, spinner: number, now: number): BottleState {
  return {
    ...g, phase: 'spin', spinner, target: null, chooser: null, card: null,
    claps: [], skipVotes: [], endsAt: now + SPIN_IDLE_MS,
  };
}

/**
 * Close the current turn: count it toward the round, end the game when the
 * rounds are done, otherwise `nextSpinner` spins.
 */
function finishTurn(g: BottleState, ctx: Ctx, nextSpinner: number): BottleState {
  const players = seatedList(ctx.seats).length;
  let round = g.round, turnInRound = g.turnInRound + 1;
  if (turnInRound >= Math.max(1, players)) { round++; turnInRound = 0; }
  const next = { ...g, round, turnInRound, turnNo: g.turnNo + 1 };
  if (g.rounds > 0 && round > g.rounds) return endGame(next, 'rounds');
  const spinner = ctx.seats[nextSpinner] ? nextSpinner : nextSeated(ctx.seats, nextSpinner);
  return toSpin(next, spinner, ctx.now);
}

function endGame(g: BottleState, why: BottleState['ended']): BottleState {
  return { ...g, phase: 'over', endsAt: null, ended: why, skipVotes: [] };
}

function award(g: BottleState, seat: number, pts: number, patch: Partial<BottlePlayer> = {}): BottleState['players'] {
  const p = g.players[seat];
  if (!p) return g.players;
  return { ...g.players, [seat]: { ...p, ...patch, score: p.score + pts } };
}

function dealCard(g: BottleState, type: CardType, ctx: Ctx): BottleState {
  const { card, used } = drawCard(g, type, ctx.rng);
  return { ...g, phase: 'card', card, used, skipVotes: [], endsAt: ctx.now + CARD_MS };
}

/** Re-check the vote thresholds (after a vote, or when someone leaves). */
function settleVotes(g: BottleState, ctx: Ctx): BottleState {
  const players = seatedList(ctx.seats).length;
  if (majority(g.endVotes.length, players)) return endGame(g, 'vote');
  if (g.phase === 'card' && g.card && majority(g.skipVotes.length, players)) return dealCard(g, g.card.type, ctx);
  return g;
}

// ---- moves ---------------------------------------------------------------------

export function applyMove(g: BottleState, actor: number, m: BottleMove, ctx: Ctx): BottleState {
  if (g.phase === 'over') throw new RuleError('The game is over');
  if (!ctx.seats[actor]) throw new RuleError('Take a seat first');
  const type = m?.type;

  if (type === 'end') {
    const on = m.on !== false;
    const endVotes = on ? (g.endVotes.includes(actor) ? g.endVotes : [...g.endVotes, actor]) : without(g.endVotes, actor);
    return settleVotes({ ...g, endVotes }, ctx);
  }

  if (type === 'spin') {
    if (g.phase !== 'spin') throw new RuleError('Not time to spin');
    if (actor !== g.spinner) throw new RuleError('Not your spin');
    return spinNow(g, ctx, Number(m.power));
  }

  if (type === 'choose') {
    if (g.phase !== 'choose') throw new RuleError('Not time to choose');
    if (actor !== g.chooser) throw new RuleError(g.mode === 'spin' ? 'The spinner picks' : 'Not your pick');
    if (m.pick !== 'truth' && m.pick !== 'dare') throw new RuleError('Pick Truth or Dare');
    return dealCard(g, m.pick, ctx);
  }

  if (type === 'done' || type === 'pass') {
    if (g.phase !== 'card' || !g.card || g.target === null) throw new RuleError('No card to answer');
    if (actor !== g.target) throw new RuleError('It is not your card');
    const t = g.target, p = g.players[t];
    if (type === 'pass') {
      if (!p || p.passes >= g.passLimit) throw new RuleError('No passes left');
      const next = { ...g, players: award(g, t, 0, { passes: p.passes + 1 }),
        last: { seat: t, name: p.name, result: 'pass' as const, type: g.card.type, points: 0 } };
      return finishTurn(next, ctx, t);
    }
    const pts = g.card.type === 'dare' ? 2 : 1;
    const players = award(g, t, pts, g.card.type === 'dare' ? { dares: (p?.dares ?? 0) + 1 } : { truths: (p?.truths ?? 0) + 1 });
    return { ...g, phase: 'cheer', players, skipVotes: [], endsAt: ctx.now + CHEER_MS,
      last: { seat: t, name: p?.name ?? '', result: 'done', type: g.card.type, points: pts + g.claps.length } };
  }

  if (type === 'clap') {
    if ((g.phase !== 'card' && g.phase !== 'cheer') || g.target === null) throw new RuleError('Nothing to clap for yet');
    if (actor === g.target) throw new RuleError('You can\'t clap for yourself');
    if (g.claps.includes(actor)) throw new RuleError('You already clapped this turn');
    const p = g.players[g.target];
    const players = award(g, g.target, 1, { claps: (p?.claps ?? 0) + 1 });
    const last = g.last && g.phase === 'cheer' && g.last.seat === g.target ? { ...g.last, points: g.last.points + 1 } : g.last;
    return { ...g, players, claps: [...g.claps, actor], last };
  }

  if (type === 'skip') {
    if (g.phase !== 'card' || !g.card) throw new RuleError('No card to skip');
    if (g.skipVotes.includes(actor)) throw new RuleError('You already voted to skip');
    return settleVotes({ ...g, skipVotes: [...g.skipVotes, actor] }, ctx);
  }

  throw new RuleError('Unknown move');
}

/** The spinner spins (or the clock spins for them): the server picks the target. */
function spinNow(g: BottleState, ctx: Ctx, power: number): BottleState {
  const to = pickTarget(ctx.seats, g.spinner, ctx.rng);
  const p = Number.isFinite(power) ? Math.min(1, Math.max(0, power)) : 0.5;
  const turns = 3 + Math.floor(ctx.rng() * 2) + Math.round(p * 2);
  const ms = 2600 + turns * 380;
  const spin: Spin = { n: (g.spin?.n ?? 0) + 1, by: g.spinner, to, turns, ms, at: ctx.now };
  return { ...g, phase: 'spinning', spin, target: to, chooser: g.mode === 'spin' ? g.spinner : to,
    card: null, claps: [], skipVotes: [], endsAt: ctx.now + ms };
}

// ---- the game clock -------------------------------------------------------------

export function wake(g: BottleState, ctx: Ctx): BottleState {
  switch (g.phase) {
    case 'spin': return seatedList(ctx.seats).length >= 2 ? spinNow(g, ctx, 0.5) : { ...g, endsAt: ctx.now + SPIN_IDLE_MS };
    case 'spinning': return { ...g, phase: 'choose', endsAt: ctx.now + CHOOSE_MS };
    case 'choose': return dealCard(g, ctx.rng() < 0.5 ? 'truth' : 'dare', ctx);
    case 'card': {
      // Time's up: no points, no pass used, the target still spins next.
      const t = g.target!, p = g.players[t];
      return finishTurn({ ...g, last: { seat: t, name: p?.name ?? '', result: 'timeout', type: g.card?.type ?? null, points: 0 } }, ctx, t);
    }
    case 'cheer': return finishTurn(g, ctx, g.target ?? g.spinner);
    default: return g;
  }
}

// ---- join / leave -------------------------------------------------------------

export function join(g: BottleState, seat: number, ctx: Ctx): BottleState {
  const p = newPlayer(ctx, seat, g.away);
  const away = { ...g.away };
  if (p.uid) delete away[p.uid];
  return { ...g, players: { ...g.players, [seat]: p }, away };
}

export function leave(g: BottleState, seat: number, ctx: Ctx): BottleState {
  const p = g.players[seat];
  const players = { ...g.players };
  delete players[seat];
  const away = { ...g.away };
  if (p?.uid) { const { uid: _u, name: _n, ...stats } = p; away[p.uid] = stats; }
  let next: BottleState = {
    ...g, players, away,
    claps: without(g.claps, seat), skipVotes: without(g.skipVotes, seat), endVotes: without(g.endVotes, seat),
  };
  if (next.phase === 'over') return next;
  if (seatedList(ctx.seats).length < 2) return endGame(next, 'players');

  if (next.phase === 'spin' && next.spinner === seat) {
    next = toSpin(next, nextSeated(ctx.seats, seat), ctx.now);
  } else if (next.target === seat && next.phase !== 'spin') {
    // The player the bottle chose left: the turn is over; the spinner goes again.
    // (Already done and being cheered: keep the result line.)
    const last = next.phase === 'cheer' || !p ? next.last : { seat, name: p.name, result: 'left' as const, type: next.card?.type ?? null, points: 0 };
    next = finishTurn({ ...next, last },
      ctx, ctx.seats[next.spinner] ? next.spinner : nextSeated(ctx.seats, seat));
  } else if (next.phase === 'choose' && next.chooser === seat) {
    next = { ...next, chooser: next.target };
  }
  return next.phase === 'over' ? next : settleVotes(next, ctx);
}

// ---- results --------------------------------------------------------------------

/** Standings: seated players by score (ties share a place). */
export function standings(g: BottleState, seats: Ctx['seats']): { seat: number; name: string; score: number; place: number }[] {
  const rows = seatedList(seats).map((seat) => ({ seat, name: g.players[seat]?.name ?? seats[seat]?.name ?? '', score: g.players[seat]?.score ?? 0 }));
  rows.sort((a, b) => b.score - a.score || a.seat - b.seat);
  return rows.map((r) => ({ ...r, place: rows.findIndex((x) => x.score === r.score) + 1 }));
}

export function resultsOf(g: BottleState, ctx: Ctx): SeatResult[] {
  const rows = standings(g, ctx.seats);
  const top = rows.length ? rows[0].score : 0;
  const tied = rows.filter((r) => r.score === top).length;
  return rows.map((r) => ({
    seat: r.seat,
    outcome: r.score === top ? (tied > 1 ? 'draw' : 'win') : 'loss',
    points: r.score,
  }));
}

export function newGame(ctx: Ctx): BottleState {
  const o = readOptions(ctx.options);
  const seated = seatedList(ctx.seats);
  const players: BottleState['players'] = {};
  for (const s of seated) players[s] = newPlayer(ctx, s);
  const spinner = seated[Math.min(seated.length - 1, Math.floor(ctx.rng() * seated.length))] ?? 0;
  const g: BottleState = {
    phase: 'spin', mode: o.mode, spice: o.spice, rounds: o.rounds, passLimit: PASS_LIMIT,
    round: 1, turnInRound: 0, turnNo: 0,
    spinner, target: null, chooser: null, spin: null, card: null,
    claps: [], skipVotes: [], endVotes: [], endsAt: null,
    players, away: {}, used: { truth: [], dare: [] }, last: null, ended: null,
  };
  return toSpin(g, spinner, ctx.now);
}

const step = (g: BottleState): GameStep<BottleState> => ({ g });

export const bottleGame: GameDef<BottleState, BottleMove> = {
  kind: 'bottle',
  label: 'Spin the Bottle',
  minPlayers: MIN_PLAYERS,
  maxPlayers: MAX_PLAYERS,
  joinMidGame: true,
  turnTimeoutMs: null, // the game clock handles idle players
  configure: (cur, patch) => {
    if (patch === null || typeof patch !== 'object') throw new RuleError('Bad options');
    const p = patch as Record<string, unknown>;
    const base = readOptions(cur ?? DEFAULT_OPTIONS);
    if ('mode' in p && p.mode !== 'tod' && p.mode !== 'spin') throw new RuleError('Unknown mode');
    if ('spice' in p && p.spice !== 'mild' && p.spice !== 'silly') throw new RuleError('Unknown spice level');
    if ('rounds' in p && !(ROUND_CHOICES as readonly number[]).includes(p.rounds as number)) throw new RuleError('Unknown round count');
    return readOptions({ ...base, ...p });
  },
  start: (_prev, ctx) => step(newGame(ctx)),
  turnSeat: (g) => (g.phase === 'spin' ? g.spinner : g.phase === 'choose' ? g.chooser : g.phase === 'card' ? g.target : null),
  // Everyone at the circle can act (clap, skip votes, end votes); move() checks who may do what.
  canAct: (g, actor) => actor >= 0 && g.phase !== 'over',
  move: (g, _privs: Privs, actor, m, ctx) => step(applyMove(g, actor, m, ctx)),
  wakeAt: (g) => (g.phase === 'over' ? null : g.endsAt),
  onWake: (g, _privs, ctx) => step(wake(g, ctx)),
  onJoin: (g, _privs, seat, ctx) => step(join(g, seat, ctx)),
  onLeave: (g, _privs, seat, ctx) => step(leave(g, seat, ctx)),
  isOver: (g) => g.phase === 'over',
  results: (g, ctx) => resultsOf(g, ctx),
  describe: (m, who) => {
    switch (m?.type) {
      case 'spin': return `${who} spun the bottle`;
      case 'choose': return `${who} picked ${m.pick === 'dare' ? 'Dare' : 'Truth'}`;
      case 'done': return `${who} did it!`;
      case 'pass': return `${who} passed`;
      case 'clap': return `${who} clapped`;
      case 'skip': return `${who} voted to skip the card`;
      case 'end': return m.on === false ? `${who} wants to keep playing` : `${who} voted to end the game`;
      default: return who;
    }
  },
};
